'use client';

import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

// Reveal -fades + lifts its children once it scrolls into view. One-shot:
// after the first intersection it disconnects and stays visible.
// `delay` lets a parent grid stagger its children (60-80ms steps look right).
export function Reveal({
  children,
  delay = 0,
  distance = 18,
  duration = 800,
  className,
  style,
  as: Tag = 'div',
}: {
  children: ReactNode;
  delay?: number;
  distance?: number;
  duration?: number;
  className?: string;
  style?: CSSProperties;
  as?: 'div' | 'section' | 'article' | 'li' | 'ul' | 'ol' | 'header';
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    // Skip the entrance animation on small screens or when the user prefers
    // reduced motion -saves IntersectionObserver setup and avoids a paint
    // gap on slow mobile devices.
    const mobile = window.matchMedia('(max-width: 960px)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (mobile || reduced) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const composedStyle: CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translate3d(0,0,0)' : `translate3d(0, ${distance}px, 0)`,
    transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: visible ? 'auto' : 'opacity, transform',
    ...style,
  };

  // refs for non-`div` tags need cast since the ref type narrows per element.
  const refProp = { ref: ref as React.RefObject<never> };

  return (
    <Tag {...refProp} className={className} style={composedStyle}>
      {children}
    </Tag>
  );
}
